import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { connectState } from '@examdojo/angular/util';
import { distinctUntilChanged, map, Observable, startWith, switchMap } from 'rxjs';
import { Swiper } from 'swiper/types';
import { IconButtonComponent } from '@examdojo/core/icon-button';

@Component({
  selector: 'dojo-swiper-progress-autoplay',
  imports: [IconButtonComponent, MatButtonModule],
  templateUrl: './swiper-progress-autoplay.component.html',
  styleUrl: './swiper-progress-autoplay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwiperProgressAutoplayComponent {
  readonly swiperInstance = input.required<Swiper>();

  private readonly swiperSlideChanges$ = toObservable(this.swiperInstance).pipe(
    switchMap((swiper) => {
      return new Observable<Swiper>((observer) => {
        const callback = () => observer.next(swiper);

        swiper.on('slideChange', callback);
        swiper.on('afterInit', callback);
        swiper.on('slidesLengthChange', callback);

        callback();

        return () => {
          swiper.off('slideChange', callback);
          swiper.off('afterInit', callback);
          swiper.off('slidesLengthChange', callback);
        };
      });
    }),
  );

  private readonly autoplayProgress$ = toObservable(this.swiperInstance).pipe(
    switchMap(
      (swiper) =>
        new Observable<number>((observer) => {
          // WARNING: the `autoplayTimeLeft` event in Swiper is not returning the correct `percentage` on Safari.
          const callback = (_: unknown, _timeLeft: number, percentage: number) => {
            percentage = Math.round(percentage * 100);
            const progress = Math.min(100 - percentage, 100);
            observer.next(progress);
          };

          swiper.on('autoplayTimeLeft', callback);
          return () => swiper.off('autoplayTimeLeft', callback);
        }),
    ),
    startWith(0),
    distinctUntilChanged(),
  );

  readonly isFirstSlide$ = this.swiperSlideChanges$.pipe(map((swiper) => swiper.activeIndex === 0));

  readonly state = connectState({
    isFirstSlide: this.isFirstSlide$,
    autoplayProgress: this.autoplayProgress$,
    totalSlides: this.swiperSlideChanges$.pipe(
      switchMap((swiper) => [Array(swiper.slides.length).fill(null)]),
      startWith([]),
    ),
  });

  slideBack() {
    this.swiperInstance().slidePrev();
  }

  goToSlide(index: number): void {
    const swiper = this.swiperInstance();
    if (index < 0 || index >= swiper.slides.length) {
      throw new Error(`Invalid slide index: ${index}. Must be between 0 and ${swiper.slides.length - 1}`);
    }
    swiper.slideTo(index);
  }
}
